import { getHouseNumberIncludingSuffixFromLine1 } from '@brenger/utils';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { Button, Dialog, Modal } from '../../../../brenger-shared-ui';
import { clarity } from '../../../../configs/clarity';
import { useTypedSelector } from '../../../../hooks';
import { useDebounce } from '../../../../hooks/useDebounce';
import { RootState, SessionStorageKeys, StopType } from '../../../../types';
import { floorAsString } from '../../../../utils/floors';
import { priceAsString } from '../../../../utils/price';
import { actions, getFloorLevelSuspicion, showDutchAddresLayout } from '../../ducks';
import { FloorChoice } from '../SubStepFloor';

interface Props {
  stopType: StopType;
}

export const FloorLevelSuspicionModal: React.FC<Props> = ({ stopType }) => {
  const [isModalActive, setIsModalActive] = React.useState(false);
  const dispatch = useDispatch();
  const isDutchLayout = useTypedSelector(showDutchAddresLayout);
  const address = useTypedSelector(state => state.generalTransport.contact?.[stopType]?.address);
  const houseNumber = isDutchLayout ? address?.line2 : getHouseNumberIncludingSuffixFromLine1(address?.line1 || '');
  const suspicion = useTypedSelector((state: RootState) => getFloorLevelSuspicion(state, stopType, houseNumber));
  const isFloorLevelServiceAvailable =
    useTypedSelector(state => state.generalTransport.quote?.service_advisory?.floor_service) !== 'available';
  const debouncedSuspicion = useDebounce<string>(String(suspicion?.floor), 1000);
  const storageKey =
    stopType === StopType.PICKUP ? SessionStorageKeys.FLOOR_SUS_PICKUP : SessionStorageKeys.FLOOR_SUS_DELIVERY;
  const bodyTrans = `request_flow.floor_level_suspicion.modal_body_${stopType.toLowerCase()}${
    !isFloorLevelServiceAvailable ? '_floor_service_not_available' : ''
  }`;

  React.useEffect(() => {
    const susString = JSON.stringify(suspicion);
    if (susString === sessionStorage.getItem(storageKey)) {
      setIsModalActive(false);
      return;
    }
    const shouldBeActive = Boolean(suspicion);
    if (shouldBeActive) {
      sessionStorage.setItem(storageKey, susString);
      clarity.setTag('contact-step_floor-level-suspicion');
    }
    setIsModalActive(shouldBeActive);
  }, [debouncedSuspicion]);

  const applySuspicion = (choice: FloorChoice): void => {
    setIsModalActive(false);
    dispatch(actions.setChoice({ [`${stopType}_floor`]: choice }));
  };
  if (!isModalActive) {
    return null;
  }
  return (
    <Dialog isActive={true}>
      <Modal
        onlyToggleByButton={true}
        open={true}
        size={'small'}
        header={
          <Translate
            id={'request_flow.floor_level_suspicion.modal_title'}
            data={{
              floor_count: floorAsString(suspicion?.floor),
            }}
          />
        }
      >
        <Translate
          id={bodyTrans}
          data={{
            floor_count: floorAsString(suspicion?.floor),
          }}
        />
        {isFloorLevelServiceAvailable && (
          <div className="pt-[1em] text-center">
            <Button
              buttonStyle="secondary"
              onClick={() => applySuspicion({ floor_level: suspicion?.floor || 0, elevator: false })}
            >
              <Translate
                id="request_flow.floor_level_suspicion.has_floor"
                data={{
                  floor_count: floorAsString(suspicion?.floor),
                  price: priceAsString({ price: { amount: suspicion?.floorFee || 0 } }),
                }}
              />
            </Button>
            <Button buttonStyle="secondary" onClick={() => applySuspicion({ floor_level: 0, elevator: true })}>
              <Translate
                id="request_flow.floor_level_suspicion.has_elevator"
                data={{
                  price: priceAsString({ price: { amount: suspicion?.elevatorFee || 0 } }),
                }}
              />
            </Button>
            <Button buttonStyle="tertiary" fullWidth={true} onClick={() => setIsModalActive(false)}>
              <Translate
                id={`request_flow.floor_level_suspicion.no_consequences_${stopType}`}
                data={{
                  price: priceAsString({ price: { amount: 0 } }),
                }}
              />
            </Button>
          </div>
        )}
        {!isFloorLevelServiceAvailable && (
          <div className="pt-[1em] text-center">
            <Button buttonStyle="primary" fullWidth={true} onClick={() => setIsModalActive(false)}>
              <Translate id={`request_flow.floor_level_suspicion.modal_continue_without_service`} />
            </Button>
          </div>
        )}
      </Modal>
    </Dialog>
  );
};
